import { useEffect, useMemo, useRef } from 'react';

import {
  WorkflowTracker,
  WorkflowTrackerKind,
  WorkflowTrackerStatus,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

export interface UseOnTrackerCompleteOptions {
  /**
   * The kind of workflow tracker to listen for
   */
  kind: WorkflowTrackerKind;
  /**
   * Callback to execute when a workflow tracker of the specified kind completes
   */
  onCompleted: (completedTrackerIDs: string[]) => void;
  /**
   * The workflow trackers to monitor.
   */
  trackers: WorkflowTracker[];
  /**
   * Whether the initial workflow tracker query is loading.
   */
  loading: boolean;
  /**
   * Whether the hook is enabled. Defaults to true.
   */
  enabled?: boolean;
  /**
   * Optional filter function to only track specific workflow trackers.
   * If provided, only trackers matching this predicate will be monitored for completion.
   */
  filter?: (tracker: WorkflowTracker) => boolean;
  /**
   * Label used in diagnostics to distinguish tracker sources.
   */
  sourceLabel: 'currentUser' | 'household';
}

export function useOnTrackerComplete({
  kind,
  onCompleted,
  trackers,
  loading,
  enabled = true,
  filter,
  sourceLabel,
}: UseOnTrackerCompleteOptions) {
  const trackerStatusesByID = useMemo(
    () =>
      new Map(
        trackers
          .filter(
            (tracker) => tracker.kind === kind && (!filter || filter(tracker))
          )
          .map((tracker) => [tracker.id, tracker.status])
      ),
    [filter, kind, trackers]
  );
  const previousTrackerStatusesByIDRef = useRef(trackerStatusesByID);
  const hasLoadedInitialTrackersRef = useRef(!loading);

  // Use a ref to store the latest callback to avoid triggering effects
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  // Track previous values to detect transitions that invalidate comparison
  const wasEnabledRef = useRef(enabled);
  const prevKindRef = useRef(kind);
  const prevFilterRef = useRef(filter);

  useEffect(() => {
    // Detect transitions that invalidate the previous snapshot
    const kindChanged = prevKindRef.current !== kind;
    const filterChanged = prevFilterRef.current !== filter;
    const becameEnabled = !wasEnabledRef.current && enabled;

    // Update transition refs
    prevKindRef.current = kind;
    prevFilterRef.current = filter;
    wasEnabledRef.current = enabled;

    // If disabled, baseline to stay in sync (don't diff, don't fire)
    if (!enabled) {
      diagnostics.debug('[useOnTrackerComplete] Hook is disabled', {
        kind,
        sourceLabel,
      });
      previousTrackerStatusesByIDRef.current = trackerStatusesByID;
      return;
    }

    // On transitions that invalidate comparison, baseline and return (no retroactive callback)
    if (becameEnabled || kindChanged || filterChanged) {
      diagnostics.debug('[useOnTrackerComplete] Baselining due to transition', {
        kind,
        becameEnabled,
        kindChanged,
        filterChanged,
        sourceLabel,
      });
      previousTrackerStatusesByIDRef.current = trackerStatusesByID;
      hasLoadedInitialTrackersRef.current =
        hasLoadedInitialTrackersRef.current || !loading;
      return;
    }

    if (!hasLoadedInitialTrackersRef.current) {
      previousTrackerStatusesByIDRef.current = trackerStatusesByID;
      hasLoadedInitialTrackersRef.current = !loading;
      return;
    }

    const previousTrackerStatusesByID = previousTrackerStatusesByIDRef.current;

    diagnostics.debug(
      '[useOnTrackerComplete] Checking for completed trackers',
      {
        kind,
        currentCount: trackerStatusesByID.size,
        previousCount: previousTrackerStatusesByID.size,
        currentIDs: Array.from(trackerStatusesByID.keys()),
        previousIDs: Array.from(previousTrackerStatusesByID.keys()),
        hasFilter: Boolean(filter),
        sourceLabel,
      }
    );

    const newlyCompletedTrackerIDs = Array.from(trackerStatusesByID.entries())
      .filter(
        ([trackerID, status]) =>
          status === WorkflowTrackerStatus.Completed &&
          previousTrackerStatusesByID.get(trackerID) !==
            WorkflowTrackerStatus.Completed
      )
      .map(([trackerID]) => trackerID);
    const removedTrackerIDs = Array.from(previousTrackerStatusesByID.entries())
      .filter(
        ([trackerID, status]) =>
          status !== WorkflowTrackerStatus.Completed &&
          !trackerStatusesByID.has(trackerID)
      )
      .map(([trackerID]) => trackerID);
    const completedTrackerIDs = Array.from(
      new Set([...newlyCompletedTrackerIDs, ...removedTrackerIDs])
    );

    // Update baseline before calling callback to prevent repeated detections if callback throws
    previousTrackerStatusesByIDRef.current = trackerStatusesByID;

    // If any trackers completed, call the callback
    if (completedTrackerIDs.length > 0) {
      diagnostics.debug(
        '[useOnTrackerComplete] Detected completed trackers, calling callback',
        {
          kind,
          completedTrackerIDs,
          completedCount: completedTrackerIDs.length,
          sourceLabel,
        }
      );
      onCompletedRef.current(completedTrackerIDs);
    }
  }, [enabled, filter, kind, loading, sourceLabel, trackerStatusesByID]);
}
